import { log2p1 } from './transforms';
import {
  SAMPLE_GROUPS, GROUP_MARKERS, STRIP_GROUP_ORDER,
  IMMGEN_COLORS,
} from './colors';

// X positions for 8 groups
const X_POS = {};
STRIP_GROUP_ORDER.forEach((g, i) => { X_POS[g] = i; });

export function jitter(x, n, seed = 42) {
  const vals = [];
  let s = seed;
  for (let i = 0; i < n; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    vals.push(x + (s / 0x7fffffff - 0.5) * 0.3);
  }
  return vals;
}

/**
 * Compute Plotly-ready data for a single gene strip plot.
 * Returns { traces, shapes, yRange } or null if gene not found.
 */
export function computeStripData({ gene, perSample, expression9col }) {
  if (!perSample || !perSample[gene]) return null;

  const samples = perSample[gene];
  const ref9col = expression9col[gene];
  const nGroups = STRIP_GROUP_ORDER.length;

  // Group samples
  const grouped = {};
  for (const g of STRIP_GROUP_ORDER) grouped[g] = [];
  for (const [sampleName, tpm] of Object.entries(samples)) {
    const group = SAMPLE_GROUPS[sampleName];
    if (group && grouped[group]) {
      grouped[group].push({ sample: sampleName, value: log2p1(tpm) });
    }
  }

  // Build traces
  const traces = [];
  for (const group of STRIP_GROUP_ORDER) {
    const points = grouped[group];
    if (points.length === 0) continue;
    const marker = GROUP_MARKERS[group];
    const xBase = X_POS[group];
    const xs = jitter(xBase, points.length, gene.charCodeAt(0) + xBase * 100);

    traces.push({
      type: 'scatter',
      mode: 'markers',
      x: xs,
      y: points.map(p => p.value),
      marker: {
        symbol: marker.symbol,
        color: marker.color,
        size: 8,
        line: { width: 0.8, color: '#fff' },
      },
      name: marker.name,
      showlegend: false,
      hovertext: points.map(p => `${p.sample}: ${p.value.toFixed(2)}`),
      hoverinfo: 'text',
    });
  }

  // ImmGen reference lines (indices 0-3 in the full expression array)
  const refEntries = [
    { idx: 0, label: 'Fo', color: IMMGEN_COLORS.Fo },
    { idx: 1, label: 'CB(DZ)', color: IMMGEN_COLORS['CB(DZ)'] },
    { idx: 2, label: 'CC(LZ)', color: IMMGEN_COLORS['CC(LZ)'] },
    { idx: 3, label: 'PB', color: IMMGEN_COLORS.PB },
  ];

  const shapes = ref9col ? refEntries.map(r => ({
    type: 'line',
    x0: -0.5, x1: nGroups - 0.5,
    y0: log2p1(ref9col[r.idx]), y1: log2p1(ref9col[r.idx]),
    line: { color: r.color, width: 1.5, dash: 'dash' },
    xref: 'x', yref: 'y',
  })) : [];

  // Y range
  const allVals = [];
  for (const group of STRIP_GROUP_ORDER) {
    for (const p of grouped[group]) allVals.push(p.value);
  }
  if (ref9col) {
    for (let i = 0; i < 4; i++) allVals.push(log2p1(ref9col[i]));
  }
  const yMin = allVals.length ? Math.min(...allVals) - 0.5 : 0;
  const yMax = allVals.length ? Math.max(...allVals) + 0.5 : 5;
  const yRange = yMax - yMin < 2
    ? [(yMin + yMax) / 2 - 1, (yMin + yMax) / 2 + 1]
    : [yMin, yMax];

  return { traces, shapes, yRange };
}
